/**
 * #765 C22 i18n — API pública del dashboard.
 *
 * F1 (2026-06-26): este archivo dejó de ser un context casero y ahora es un
 * ADAPTADOR fino sobre react-i18next (motor en `./i18n.ts`). El contrato
 * público (`I18nProvider`, `useI18n`, `useTranslation`) se mantiene IDÉNTICO
 * para no tocar ningún consumidor (App.tsx, LocaleSelector, ERP, etc.).
 *
 * Los formatters (number/currency/date) siguen siendo Intl nativo — i18n de
 * strings y formato de números son ejes distintos.
 */

import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { I18nextProvider, useTranslation as useReactI18n } from 'react-i18next';

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from './config';
import i18n from './i18n';

interface I18nContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  supportedLocales: readonly SupportedLocale[];
  t: (key: string, fallback?: string) => string;
  formatNumber: (n: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (n: number, currency?: string) => string;
  formatDate: (d: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
}

export const I18nProvider = ({ children }: { children: ReactNode }) => (
  <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
);

export const useI18n = (): I18nContextValue => {
  const { t: rt, i18n: instance } = useReactI18n('common');

  const locale = ((instance.resolvedLanguage ?? instance.language) as SupportedLocale) ?? DEFAULT_LOCALE;

  const setLocale = useCallback(
    (next: SupportedLocale) => {
      void instance.changeLanguage(next);
    },
    [instance],
  );

  const t = (key: string, fallback?: string): string =>
    rt(key, { defaultValue: fallback ?? key }) as string;

  const formatNumber = (n: number, options?: Intl.NumberFormatOptions): string => {
    try {
      return new Intl.NumberFormat(locale, options).format(n);
    } catch {
      return String(n);
    }
  };

  const formatCurrency = (n: number, currency = 'USD'): string => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n);
    } catch {
      return `${currency} ${n}`;
    }
  };

  const formatDate = (
    d: Date | string | number,
    options?: Intl.DateTimeFormatOptions,
  ): string => {
    try {
      const date = d instanceof Date ? d : new Date(d);
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      return String(d);
    }
  };

  return {
    locale,
    setLocale,
    supportedLocales: SUPPORTED_LOCALES,
    t,
    formatNumber,
    formatCurrency,
    formatDate,
  };
};

export const useTranslation = () => {
  const { t, formatNumber, formatCurrency, formatDate, locale } = useI18n();
  return { t, formatNumber, formatCurrency, formatDate, locale };
};
